var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { Player, BigPlayButton, LoadingSpinner } from 'video-react';
import HLSPlayer from '@/components/HLSPlayer';
import useLoadSource from '@/hooks/useLoadSource';
import { Box, Skeleton } from '@mui/material';
import { convertHttpsToS3Url } from '@/utils';
if (typeof window !== "undefined") {
    require("video-react/dist/video-react.css");
}
var isHLSUrl = function (url) { return url.endsWith('.m3u8'); };
var AdaptiveVideoPlayer = React.memo(function (_a) {
    var innerRef = _a.innerRef, videoUrl = _a.videoUrl, onPlayerReady = _a.onPlayerReady;
    var _b = useState(false), isHLS = _b[0], setIsHLS = _b[1];
    var _c = useState(''), videoSource = _c[0], setVideoSource = _c[1];
    var playerRef = useRef(null);
    var _d = useState(false), isLoading = _d[0], setIsLoading = _d[1];
    var getSource = useLoadSource({}).getSource;
    var _e = useState(false), isPlayerReady = _e[0], setIsPlayerReady = _e[1];
    var hlsRef = useRef(null);
    useEffect(function () { return setVideoSource(''); }, []);
    useImperativeHandle(innerRef, function () { return ({
        seekTo: function (timestamp) {
            if (!playerRef.current)
                return;
            var player = playerRef.current.getState().player;
            if (isHLS) {
                if (hlsRef === null || hlsRef === void 0 ? void 0 : hlsRef.current) {
                    if (player.paused) {
                        playerRef.current.play();
                    }
                    setTimeout(function () {
                        var _a;
                        (_a = hlsRef === null || hlsRef === void 0 ? void 0 : hlsRef.current) === null || _a === void 0 ? void 0 : _a.seekTo(timestamp);
                    }, 100);
                }
            }
            else {
                if (!player.hasStarted) {
                    playerRef.current.load();
                    setTimeout(function () {
                        if (!playerRef.current)
                            return;
                        playerRef.current.seek(timestamp);
                        playerRef.current.play();
                    }, 100);
                }
                else {
                    playerRef.current.seek(timestamp);
                    if (player.paused) {
                        playerRef.current.play();
                    }
                }
            }
        }
    }); });
    useEffect(function () {
        if (!playerRef.current)
            return;
        var handleStateChange = function (state) {
            var isReady = state.hasStarted ||
                (state.waiting === false &&
                    state.videoWidth > 0 &&
                    state.videoHeight > 0);
            if (isReady && !isPlayerReady) {
                setIsPlayerReady(true);
                onPlayerReady === null || onPlayerReady === void 0 ? void 0 : onPlayerReady();
            }
        };
        var player = playerRef.current;
        player.subscribeToStateChange(handleStateChange);
        return function () {
            if (player && player.unsubscribeToStateChange) {
                player.unsubscribeToStateChange(handleStateChange);
            }
        };
    }, [playerRef.current, isPlayerReady, onPlayerReady]);
    useEffect(function () {
        var checkVideoAvailability = function () { return __awaiter(void 0, void 0, void 0, function () {
            var s3Url, hls, source;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setIsLoading(true);
                        s3Url = convertHttpsToS3Url(videoUrl);
                        if (!s3Url) return [3 /*break*/, 3];
                        hls = isHLSUrl(s3Url);
                        setIsHLS(hls);
                        if (!hls) return [3 /*break*/, 1];
                        setIsLoading(false);
                        setVideoSource(s3Url);
                        return [3 /*break*/, 3];
                    case 1: return [4 /*yield*/, getSource(s3Url)];
                    case 2:
                        source = _a.sent();
                        setIsLoading(false);
                        setVideoSource(source);
                        _a.label = 3;
                    case 3: return [2 /*return*/];
                }
            });
        }); };
        if (!videoSource)
            checkVideoAvailability();
    }, [videoUrl]);
    if (isLoading) {
        return _jsx(Box, { sx: { width: '100%', height: 200, position: 'relative', alignItems: 'center', justifyContent: 'center' }, className: "video-player-container", children: _jsx(Skeleton, { variant: "rectangular", width: "100%", height: "100%" }) });
    }
    if (!videoSource) {
        return _jsx("div", { children: "Error: Unable to load video" });
    }
    return (_jsx("div", { className: "video-player-container", children: isHLS ? (_jsxs(Player, { ref: playerRef, children: [_jsx(HLSPlayer, { src: videoSource, innerRef: hlsRef, isVideoChild: true }), _jsx(BigPlayButton, { position: "center" }), _jsx(LoadingSpinner, {})] })) : (_jsxs(Player, { ref: playerRef, fluid: true, preload: "metadata", src: videoSource, children: [_jsx(BigPlayButton, { position: "center" }), _jsx(LoadingSpinner, {})] })) }));
});
export default AdaptiveVideoPlayer;
